'use client';

import { createContext, useContext } from 'react';
import type { SemesterItem } from '@/app/types/interfaces';
import type { CatalogIndex, PlannerProgram, PlannerRequirement, PlannerTerm, buildPlacement } from './plannerModel';
import type { ProposalTerm } from './planProposal';

export interface PlanIssue {
  type: string;
  course: string;
  issue: string;
  suggestion?: string;
}

export type PlannerOverlay =
  | { kind: 'completion'; requirementKey?: string }
  | { kind: 'course'; code: string; anchor: HTMLElement | null }
  | { kind: 'list'; semesterNumber: number; index: number; anchor: HTMLElement | null }
  | { kind: 'slot'; requirementKey: string; semesterNumber?: number; anchor: HTMLElement | null }
  | { kind: 'issues'; anchor: HTMLElement | null }
  | { kind: 'move'; semesterNumber: number; index: number; anchor: HTMLElement | null }
  | { kind: 'term-add'; semesterNumber: number; anchor: HTMLElement | null };

/** A course list the student saved in Course lists (not a degree requirement list). */
export interface UserCourseList {
  id: string;
  name: string;
  /** Normalized course codes. */
  classes: string[];
}

/** Placeholder items for a saved list use this mapping name so they never collide with degree lists. */
export const userListMappingName = (id: string) => `user-list:${id}`;

export interface PlannerActions {
  addCourse: (semesterNumber: number, code: string) => void;
  addList: (semesterNumber: number, listName: string, label: string) => void;
  moveItem: (fromSemesterNumber: number, index: number, toSemesterNumber: number) => void;
  removeItem: (semesterNumber: number, index: number) => void;
  addTerm: () => void;
  removeTerm: (semesterNumber: number) => void;
  addFromSearch: (semesterNumber: number) => void;
  chooseListCourse: (item: SemesterItem, semesterNumber: number, index: number) => void;
  askAboutCourse: (code: string, termName?: string | null) => void;
  importDegree: () => void;
  addUserListPlaceholder: (semesterNumber: number, list: UserCourseList) => void;
  acceptProposal: (accept: 'all' | string[]) => void;
  discardProposal: () => void;
}

/** How much room finished work takes: full cards, a one-line summary, or nothing. */
export type CompletedMode = 'show' | 'compact' | 'hide';

export interface PlannerContextValue {
  terms: PlannerTerm[];
  programs: PlannerProgram[];
  requirementByKey: Map<string, PlannerRequirement>;
  placement: ReturnType<typeof buildPlacement>;
  completed: Set<string>;
  index: CatalogIndex;
  issues: PlanIssue[];
  issuesByCode: Map<string, PlanIssue[]>;
  earnedCredits: number;
  nameOf: (code: string) => string;
  creditsOf: (code: string) => number;
  listLabel: (listName: string) => string;
  listCredits: (listName: string) => number;
  selectedCode: string | null;
  focusRequirementKey: string | null;
  setFocusRequirementKey: (key: string | null) => void;
  open: (overlay: PlannerOverlay) => void;
  close: () => void;
  actions: PlannerActions;
  /** Pending auto-fill additions, already diffed against the plan. Empty when there is none. */
  proposal: ProposalTerm[];
  proposalBusy: boolean;
  userLists: UserCourseList[];
  /** null while loading; false when lists can't be shown (signed out or failed). */
  userListsReady: boolean | null;
  completedMode: CompletedMode;
  /** null shows the entire plan; a degree id narrows Flowsheet and Degree to that program. */
  programScope: number | null;
}

export const PlannerContext = createContext<PlannerContextValue | null>(null);

export function usePlanner(): PlannerContextValue {
  const value = useContext(PlannerContext);
  if (!value) throw new Error('usePlanner must be used inside PlannerContext');
  return value;
}
